import formatMoney from '../../core/formatMoney';
import { handleize, escapeHtml } from '../../core/helpers';
import { getSizedImageURL } from '../../core/images';
import getProductURL from '../../core/productURL';
import { getRatingHTML, extractRatingData } from '../../components/rating';

const CONSTANTS = {
  MONEY_FORMAT: theme.moneyFormat,
  DEFAULT_ALT_TEXT: {
    FRONT: 'Front of',
    BACK: 'Back of',
    WITH_COLOR: 'with color',
  }
};

// DOM caching mechanism to avoid repeated DOM queries
// Executes the DOM query once and caches the retrieved results synchronously.
// To ensure we don't hold outdated references or create memory leaks (especially if Shopify PJAX or filters swap out page content dynamically without a full reload), the cache clears itself naturally at the end of the JavaScript execution tick using Promise.resolve().then().
let localDOMCache = null;
const getCachedDOM = (key, selector, extractFn) => {
  if (!localDOMCache) {
    localDOMCache = {};
    Promise.resolve().then(() => {
      localDOMCache = null;
    });
  }
  if (localDOMCache[key] === undefined) {
    const el = document.querySelector(selector);
    localDOMCache[key] = el ? extractFn(el) : null;
  }
  return localDOMCache[key];
};

/* Different promo message for logged-in/loyalty and guest customers
   Also update components/quicklook.js and snippets/product-details.liquid
 */
const promoForTile = (product, tile_settings) => {
  if (window.theme.promoMessage.usOnly && window.Shopify.country !== 'US') {
    return '';
  }

  const is_loyalty_member = (typeof window.theme.customer.id !== 'undefined');
  let final_promo_html = '';
  const promo_tags_metafield = product.custom_fields['astr-promo_tags'] ? product.custom_fields['astr-promo_tags'].split(',') : [];

  if (promo_tags_metafield.length === 0) {
    return final_promo_html;
  }

  /**************************************/
  /************ Build Values ************/
  /**************************************/

  // Promo applied by product tag or collection. Ignored when a promotion call out message has been defined for all products
  let promo_message = window.theme.promoMessage.guests;
  let promo_message_color = window.theme.promoMessage.color;
  let promo_discount = window.theme.promoMessage.guestPercentOff;
  let promo_discount_color = window.theme.promoMessage.colorPercentOff;
  let promo_tag = window.theme.promoMessage.tag.split(',');

  // Variables for 2nd tag support
  let promo_message_2 = window.theme.promoMessage.guests2;
  const promo_message_color_2 = window.theme.promoMessage.color2;
  let promo_discount_2 = window.theme.promoMessage.guestPercentOff2;
  let promo_discount_color_2 = window.theme.promoMessage.colorPercentOff2;
  let promo_tag_2 = window.theme.promoMessage.tag2.split(',');

  // let special_message = '';

  // Promo applied to all products.
  if (window.theme.promoMessage.guestsAllProducts !== '') {
    promo_message = window.theme.promoMessage.guestsAllProducts;
    promo_message_color = window.theme.promoMessage.colorAllProducts;
  }

  if (window.theme.promoMessage.guestAllProductsPercentOff !== 0) {
    promo_discount = window.theme.promoMessage.guestAllProductsPercentOff;
    promo_discount_color = window.theme.promoMessage.colorAllProductsPercentOff;
  }

  if (window.theme.promoMessage.guestsAllProducts !== '' || window.theme.promoMessage.guestAllProductsPercentOff !== 0) {
    promo_tag = [];
    promo_tag_2 = [];
  }

  // Promo applied to loyalty members
  if (is_loyalty_member) {
    promo_message = window.theme.promoMessage.loyalty;
    promo_discount = window.theme.promoMessage.loyaltyPercentOff;
    promo_discount_color = window.theme.promoMessage.colorPercentOff;

    promo_message_2 = window.theme.promoMessage.loyalty2;
    promo_discount_2 = window.theme.promoMessage.loyaltyPercentOff2;
    promo_discount_color_2 = window.theme.promoMessage.colorPercentOff2;

    // Promo applied to all products.
    if (window.theme.promoMessage.loyaltyAllProducts !== '') {
      promo_message = window.theme.promoMessage.loyaltyAllProducts;
      promo_message_color = window.theme.promoMessage.colorAllProducts;
    }

    if (window.theme.promoMessage.loyaltyAllProductsPercentOff !== 0) {
      promo_discount = window.theme.promoMessage.loyaltyAllProductsPercentOff;
      promo_discount_color = window.theme.promoMessage.colorAllProductsPercentOff;
    }

    if (window.theme.promoMessage.loyaltyAllProducts !== '' || window.theme.promoMessage.loyaltyAllProductsPercentOff !== 0) {
      promo_tag = [];
      promo_tag_2 = [];
    }
  }

  /*
  // Special message
  if (window.theme.promoMessage.specialMessage !== '') {
    if (is_loyalty_member) {
      if (window.theme.promoMessage.specialMessageCustomers === 'guests' || window.theme.promoMessage.specialMessageCustomers === 'all') {
        special_message = window.theme.promoMessage.specialMessage;
      }
    } else if (window.theme.promoMessage.specialMessageCustomers === 'members' || window.theme.promoMessage.specialMessageCustomers === 'all') {
      special_message = window.theme.promoMessage.specialMessage;
    }
  }

  special_message = special_message;
  */

  promo_message = promo_message;
  promo_message_2 = promo_message_2;

  const before_price_text = (window.theme.promoMessage.promoBeforeText) ? `${window.theme.promoMessage.promoBeforeText} ` : '';
  const between_price_text = (window.theme.promoMessage.promoBetweenText) ? ` ${window.theme.promoMessage.promoBetweenText} ` : '';
  const after_price_text = (window.theme.promoMessage.promoAfterText) ? ` ${window.theme.promoMessage.promoAfterText}` : '';

  /**************************************/
  /************* Build HTML *************/
  /**************************************/
  // Discount message
  let discount_message_html = '';
  if (promo_discount && promo_discount !== 0 && (product.from === '' || typeof product.from === 'undefined')) {
    discount_message_html = `<p class="product-tile__price-promo" data-product-tile-price-promo data-raw-price="${product.price.toFixed(2)}" data-promo-discount="${promo_discount}"><span class="price__promo" style="color: ${promo_discount_color} !important;">${before_price_text}${formatMoney(String((product.price - ((product.price * promo_discount) / 100)).toFixed(2)), CONSTANTS.MONEY_FORMAT)}${between_price_text}${promo_discount}%${after_price_text}</span></p>`;
  }

  let discount_message_html_2 = '';
  if (promo_discount_2 && promo_discount_2 !== 0 && (product.from === '' || typeof product.from === 'undefined')) {
    discount_message_html_2 = `<p class="product-tile__price-promo" data-product-tile-price-promo data-raw-price="${product.price.toFixed(2)}" data-promo-discount="${promo_discount_2}"><span class="price__promo" style="color: ${promo_discount_color_2} !important;">${before_price_text}${formatMoney(String((product.price - ((product.price * promo_discount_2) / 100)).toFixed(2)), CONSTANTS.MONEY_FORMAT)}${between_price_text}${promo_discount_2}%${after_price_text}</span></p>`;
  }

  // Special message, we need to make sure it only applies to the selected collection if any
  const special_message_html = '';
  /*
  if (special_message !== '') {
    let show_special_message = false;

    if (window.theme.promoMessage.specialMessageCollection === '') {
      show_special_message = true;
    } else {
      if (tile_settings.is_search === false && document.querySelector('[data-collection-grid]') !== null) {
        if (window.theme.promoMessage.specialMessageCollection === document.querySelector('[data-collection-grid]').dataset.collection) {
          show_special_message = true;
        }
      }

      if (show_special_message === false && typeof product.categories !== 'undefined' && product.categories.length > 0) {
        for (let i = 0; i < product.categories.length; i++) {
          const collection = handleize(product.categories[i]);

          if (collection === window.theme.promoMessage.specialMessageCollection) {
            show_special_message = true;
            break;
          }
        }
      }
    }

    // console.log('show_special_message', show_special_message);
    if (show_special_message === true) {
      special_message_html = `<span class="product-tile__special-message" data-same-line="${window.theme.promoMessage.specialMessageSameLine}">${special_message}</span>`;
    }
  }
  */

  // Promo message
  let promo_message_html = '';
  if (promo_message !== '') {
    promo_message_html = `<p data-product-tile-message class="product-tile__message" style="color: ${promo_message_color} !important;" tabindex="0" data-tag="1"><span>${promo_message}</span>${special_message_html}</p>`;
  }

  let promo_message_html_2 = '';
  if (promo_message_2 !== '') {
    promo_message_html_2 = `<p data-product-tile-message class="product-tile__message" style="color: ${promo_message_color_2} !important;" tabindex="0" data-tag="2"><span>${promo_message_2}</span>${special_message_html}</p>`;
  }

  if (promo_tag.length === 0) {
    // Message for all products
    final_promo_html += discount_message_html;
    final_promo_html += promo_message_html;

    if (promo_message_html === '' && special_message_html !== '') {
      final_promo_html += `<p data-product-tile-message class="product-tile__message" style="color: ${promo_message_color} !important;" tabindex="0">${special_message_html}</p>`;
    }
  } else {
    // Message for specific tagged products or collections
    let show_promo_message = false;
    let show_promo_message_2 = false;

    if (promo_tag.length > 0 || promo_tag_2.length > 0) {
      for (let i = 0; i < promo_tags_metafield.length; i++) {
        const meta_tag = promo_tags_metafield[i].trim();

        if (!show_promo_message && promo_tag.length > 0) {
          if (promo_tag.some(pt => pt.trim() !== '' && meta_tag.includes(pt.trim()))) {
            show_promo_message = true;
          }
        }

        if (!show_promo_message_2 && promo_tag_2.length > 0) {
          if (promo_tag_2.some(pt => pt.trim() !== '' && meta_tag.includes(pt.trim()))) {
            show_promo_message_2 = true;
          }
        }

        if (show_promo_message && show_promo_message_2) break;
      }
    }

    // console.log('show_promo_message', product, show_promo_message);
    if (show_promo_message === true) {
      final_promo_html += discount_message_html;
      final_promo_html += promo_message_html;

      if (promo_message_html === '' && special_message_html !== '') {
        final_promo_html += `<p data-product-tile-message class="product-tile__message" style="color: ${promo_message_color} !important;" tabindex="0">${special_message_html}</p>`;
      }
    } else if (show_promo_message_2 === true) {
      final_promo_html += discount_message_html_2;
      final_promo_html += promo_message_html_2;

      if (promo_message_html_2 === '' && special_message_html !== '') {
        final_promo_html += `<p data-product-tile-message class="product-tile__message" style="color: ${promo_message_color_2} !important;" tabindex="0">${special_message_html}</p>`;
      }
    } else if (special_message_html !== '') {
      final_promo_html += `<p data-product-tile-message class="product-tile__message" style="color:${promo_message_color} !important;" tabindex="0">${special_message_html}</p>`;
    }
  }

  return final_promo_html;
};

const featuredPromoForTile = (product, tile_settings) => {
  let final_promo_html = '';
  const is_loyalty_member = (typeof window.theme.customer.id !== 'undefined');
  const promo_tag = product.custom_fields['astr-promo_tags'] ? product.custom_fields['astr-promo_tags'].split(',') : [];
  const promo_message = (is_loyalty_member) ? window.theme.promoMessage.loyaltyMessageTilesOnly : window.theme.promoMessage.guestMessageTilesOnly;
  const promo_link = (is_loyalty_member) ? window.theme.promoMessage.loyaltyLinkTilesOnly : window.theme.promoMessage.guestLinkTilesOnly;
  const promo_tags_metafield = window.theme.promoMessage.tagTilesOnly.split(',');

  if (promo_message === '' || promo_tag.length === 0 || promo_tags_metafield.length === 0) {
    return final_promo_html;
  }

  let show_promo_message = false;

  if (promo_tag.length > 0) {
    for (let i = 0; i < promo_tags_metafield.length; i++) {
      const meta_tag = promo_tags_metafield[i].trim();

      if (promo_tag.some(pt => pt.trim() !== '' && meta_tag.includes(pt.trim()))) {
        show_promo_message = true;
        break;
      }
    }
  }

  if (show_promo_message === true) {
    if (promo_link === '') {
      final_promo_html = `<div class="product-tile__featured-promo" tabindex="0" data-product-tile-featured-message>${promo_message}</div>`;
    } else {
      final_promo_html = `<div class="product-tile__featured-promo" tabindex="0" data-product-tile-featured-message><a href="${promo_link}">${promo_message}</a></div>`;
    }
  }

  return final_promo_html;
};

const getPrimaryFlagTextForTile = (product, product_tags) => {
  if (typeof product.custom_fields !== 'undefined') {
    if (typeof product?.custom_fields?.bestseller_style_30 !== 'undefined' && parseInt(product?.custom_fields?.bestseller_style_30) <= 30) {
      return window.theme.strings.bestSellers;
    }

    if (typeof product?.custom_fields?.bestseller_style_14 !== 'undefined' && parseInt(product?.custom_fields?.bestseller_style_14) <= 30) {
      return window.theme.strings.trending;
    }
  }

  const foundTag = product_tags.find(tag => tag.includes('flag:') && !tag.toLowerCase().includes('final sale'));
  if (foundTag) {
    return foundTag.replace('flag:', '');
  }

  return '';
};

/*
  Flags based on tags.
  Limit 1 per tile.
  Priority: Best Seller app flag > Trending app flag > Style metafield flag > Tag-based flag
*/
const flagsForTile = (product, product_tags) => {
  const flagText = getPrimaryFlagTextForTile(product, product_tags);
  if (!flagText) {
    return '<div class="product-tile__flags"></div>';
  }

  const isTagFlag = product_tags.some(tag => tag.includes('flag:') && !tag.toLowerCase().includes('final sale'));
  const flagClass = isTagFlag ? 'product-tile__flag product-tile__flag--tag' : 'product-tile__flag product-tile__flag--app';
  return `<div class="product-tile__flags"><div class="${flagClass}">${flagText}</div></div>`;
};

const wishlistForTile = (product, product_handle) => {
  const is_loyalty_member = (typeof window.theme.customer.id !== 'undefined');

  return (is_loyalty_member) ?
    `<button type="button" class="no-themed add-to-wishlist-view-product product_${product.product_id}" data-product-wishlist data-product-id="${product.product_id}" data-variant-id="${product.skus[0].id}" data-product-url="${product.url}" data-product-handle="${product_handle}" data-is-init="false" aria-label="${window.theme.strings.addToWishlist}"></button>` :
    `<button type="button" class="no-themed add-to-wishlist-view-product product_${product.product_id}" data-product-wishlist data-product-handle="${product_handle}" data-open-signin-actions-modal="wishlist" aria-label="${window.theme.strings.addToWishlist}"></button>`;

  // Version using native Swym widget
  // return (!is_loyalty_member) ? `<button type="button" class="swym-button swym-add-to-wishlist-view-product product_${product.product_id} swym-heart swym-loaded" data-product-swym-wishlist data-product-handle="${product_handle}" data-open-signin-actions-modal="wishlist" aria-label="${window.theme.strings.addToWishlist}"></button>` : `<button data-with-epi="true" type="button" class="swym-button swym-add-to-wishlist-view-product product_${product.product_id}" data-product-swym-wishlist data-swaction="addToWishlist" data-product-id="${product.product_id}" data-variant-id="${product.skus[0].id}" data-product-url="${product.url}" data-product-handle="${product_handle}" aria-label="${window.theme.strings.addToWishlist}"></button>`;

  // Also re-enable data-empi on product tile in case swym is ever re-enabled
  // data-empi="${product.skus[0].id}"
};

const imagesForTile = (product, safe_product_title, tile_settings) => {
  const placeholder = window.theme.collections.placeholder;

  // Don't lazy load the product images above the fold
  const loading_type = (typeof tile_settings.tile_index !== 'undefined' && tile_settings.tile_index !== '' && tile_settings.tile_index <= 5) ? 'eager' : 'lazy';

  let images_html = `<div class="product-tile__image--placeholder"><img src="${placeholder}" alt="${safe_product_title}" width="300" height="460" /></div>`;

  images_html += `<div class="product-tile__images-slider">`;

  if ((typeof product.swatches !== 'undefined' && product.swatches.length) || typeof product.image_url === 'undefined') {
    images_html += `<div class="product-tile__image--main" data-product-main-image><img loading="${loading_type}" src="${placeholder}" alt="${safe_product_title}" width="300" height="460" /></div>`;

    if (window.theme.breakpoints.current !== 'mobile') {
      images_html += `<div class="product-tile__image--alt" data-product-alt-image><img loading="lazy" src="${placeholder}" alt="${safe_product_title}" width="300" height="460" /></div>`;
    }
  } else if (window.theme.breakpoints.current === 'mobile') {
    // images_html += `<div class="product-tile__image--main" data-product-main-image><img loading="lazy" src="${product.image_url}" width="${product.image_width}" alt="${safe_product_title}" onError="this.src = '${placeholder}';" /></div>`;

    // IMPORTANT: Don't forget to update the image within selectNewColor.js
    // Note, we don't generate the images to save on rendering time when the swatches are present since they'll be generated from selectNewColor.js

    // We have 2 slides on mobile (use 100vw for double size)
    // We have 4 slides on desktop (use 50vw for double size)

    if (tile_settings.has_swatches && !tile_settings.is_simplified) {
      images_html += `
        <div class="product-tile__image--main" data-product-main-image></div>
      `;
    } else {
      images_html += `
        <div class="product-tile__image--main" data-product-main-image>
          <img
            loading="${loading_type}"
            src="${getSizedImageURL(product.image_url, '600x')}"
            srcset="
              ${getSizedImageURL(product.image_url, '90x138')} 90w,
              ${getSizedImageURL(product.image_url, '180x276')} 180w,
              ${getSizedImageURL(product.image_url, '250x383')} 250w,
              ${getSizedImageURL(product.image_url, '400x613')} 400w,
              ${getSizedImageURL(product.image_url, '450x690')} 450w,
              ${getSizedImageURL(product.image_url, '500x767')} 500w,
              ${getSizedImageURL(product.image_url, '700x1073')} 700w,
              ${getSizedImageURL(product.image_url, '800x1200')} 800w
            "
            alt="${safe_product_title}"
            sizes="(max-width: 768px) 100vw, 50vw"
            width="180" height="276"
            data-original-src="${product.image_url}"
            onError="this.onerror=null; this.src='${placeholder}'; this.srcset='';"
          />
        </div>
      `;
    }

    if (tile_settings.has_swatches) {
      images_html += `
        <div class="product-tile__image--alt" data-product-alt-image></div>
      `;
    } else {
      images_html += `
        <div class="product-tile__image--alt" data-product-alt-image>
          <img
            loading="lazy"
            src="${getSizedImageURL(product.image_url, '600x')}"
            srcset="
              ${getSizedImageURL(product.image_url, '90x138')} 90w,
              ${getSizedImageURL(product.image_url, '180x276')} 180w,
              ${getSizedImageURL(product.image_url, '250x383')} 250w,
              ${getSizedImageURL(product.image_url, '400x613')} 400w,
              ${getSizedImageURL(product.image_url, '450x690')} 450w,
              ${getSizedImageURL(product.image_url, '500x767')} 500w,
              ${getSizedImageURL(product.image_url, '700x1073')} 700w,
              ${getSizedImageURL(product.image_url, '800x1200')} 800w
            "
            alt="${safe_product_title}"
            sizes="(max-width: 768px) 100vw, 50vw"
            width="180" height="276"
            data-original-src="${product.image_url}"
            onError="this.onerror=null; this.src='${placeholder}'; this.srcset='';"
          />
        </div>
      `;
    }
  } else if (window.theme.breakpoints.current !== 'mobile') {
    if (tile_settings.has_swatches && !tile_settings.is_simplified) {
      images_html += `
        <div class="product-tile__image--main" data-product-main-image></div>
      `;
    } else {
      images_html += `
        <div class="product-tile__image--main" data-product-main-image>
          <img
            loading="${loading_type}"
            src="${getSizedImageURL(product.image_url, '600x915')}"
            srcset="
              ${getSizedImageURL(product.image_url, '400x613')} 400w,
              ${getSizedImageURL(product.image_url, '500x767')} 500w,
              ${getSizedImageURL(product.image_url, '600x915')} 600w,
              ${getSizedImageURL(product.image_url, '800x1226')} 800w,
              ${getSizedImageURL(product.image_url, '1000x1533')} 1000w
            "
            sizes="(min-width: 1024px) 25vw, 400px"
            alt="${safe_product_title}"
            width="600" height="915"
            data-original-src="${product.image_url}"
            data-new-color-selected="false"
            onError="this.onerror=null; this.src='${placeholder}'; this.srcset='';"
          />
        </div>
      `;
    }

    // images_html += `<div class="product-tile__image--alt" data-product-alt-image><img loading="lazy" src="${product.image_url}" width="${product.image_width}" alt="${safe_product_title}" onError="this.src = '${placeholder}';" /></div>`;

    // We have 2 slides on mobile (use 100vw for double size)
    // We have 4 slides on desktop (use 50vw for double size)
    if (tile_settings.has_swatches) {
      images_html += `
        <div class="product-tile__image--alt" data-product-alt-image></div>
      `;
    } else {
      images_html += `
        <div class="product-tile__image--alt" data-product-alt-image>
          <img
            loading="lazy"
            src="${getSizedImageURL(product.image_url, '600x915')}"
            srcset="
              ${getSizedImageURL(product.image_url, '400x613')} 400w,
              ${getSizedImageURL(product.image_url, '500x767')} 500w,
              ${getSizedImageURL(product.image_url, '600x915')} 600w,
              ${getSizedImageURL(product.image_url, '800x1226')} 800w,
              ${getSizedImageURL(product.image_url, '1000x1533')} 1000w
            "
            sizes="(min-width: 1024px) 25vw, 400px"
            alt="${safe_product_title}"
            width="600" height="915"
            data-original-src="${product.image_url}"
            onError="this.onerror=null; this.src='${placeholder}'; this.srcset='';"
          />
        </div>
      `;
    }
  }

  images_html += `</div>`;

  if (tile_settings.is_simplified === true && tile_settings.has_quickview === false) {
    images_html += `<div class="product-tile__shop">SHOP NOW</div>`;
  } else {
    images_html += `<div class="product-tile__slider-indicator"><div class="product-tile__slider-indicator-thumb"></div></div>`;
  }

  return images_html;
};

// Disabled, functionality might not be working if re-enabled
const addToCartAltForTile = (product) => {
  return '';

  // return `<div class="add-to-cart-alt add-to-cart-alt--desktop btn btn--primary<% if (!product.available) { %> unavailable<% } %>" data-add-to-cart-alt-button role="button" tabindex="0" aria-label="Show product options"><span><%= window.theme.strings.addToCart %></span></div><div class="add-to-cart-alt add-to-cart-alt--mobile<% if (!product.available) { %> unavailable<% } %>" data-add-to-cart-alt-button><span><%= window.theme.strings.addToCart %></span></div>`;
};

const quickviewForTile = () => {
  return `<div class="quicklook__button btn btn--secondary hidden--mobile" data-quicklook-button data-modal="#quicklook-modal"><span>${window.theme.strings.quickView}</span></div><div class="quicklook__button--mobile" data-quicklook-button-mobile><span>${window.theme.strings.quickView}</span></div>`;
};

// This is a fallback function; On PLPs, Nosto returns the recommended variant first, which is not always the first variant in the list and might not return the correct back image
const findBackVariantImage = (product, color) => {
  try {
    let variant_back_image = '';

    // Find the next variant with a different color
    let found_same_color = false;
    for (let i = 0; i < product.skus.length; i++) {
      const find_variant = product.skus[i];
      const find_color_value = find_variant.custom_fields?.Color;

      if (find_color_value === color) {
        found_same_color = true;
        continue;
      } else if (found_same_color && variant_back_image === '') {
        const next_variant_with_different_color = product.skus[i];
        const next_front_image = next_variant_with_different_color.image_url;
        const next_front_image_index = product.skus.findIndex(sku => sku.image_url === next_front_image);

        if (next_front_image_index > 0 && typeof product.alternate_image_urls[next_front_image_index - 1] !== 'undefined') {
          variant_back_image = product.alternate_image_urls[next_front_image_index - 1];
        } else if (typeof product.alternate_image_urls === 'undefined') {
          console.log('No alternate image URLs found for product:', product.name);
        }

        found_same_color = false;
        break;
      }
    }

    // Last color variant
    if (found_same_color && variant_back_image === '') {
      variant_back_image = product.alternate_image_urls[product.alternate_image_urls.length - 1];
    }

    return variant_back_image;
  } catch (error) {
    console.error('Error finding back variant image:', error);
    return '';
  }
};

const swatchesForTile = (product, tile_settings) => {
  // Swatches metafield format
  // [
  //   {
  //       "color_name": "Black",
  //       "color_handle": "black",
  //       "swatch_value": "#0e0e0e",
  //       "swatch_type": "hex"
  //   },
  //   {
  //       "color_name": "Moss",
  //       "color_handle": "moss",
  //       "swatch_value": "https://cdn.shopify.com/s/files/1/2483/6632/t/75/assets/astr-swatch--moss-4bdb0f3e-2c14-4ca0-976c-3f1dc07850d6.png?v=1726505940",
  //       "swatch_type": "image"
  //   }
  // ]

  let swatches_json = [];
  if (typeof product.custom_fields !== 'undefined' && typeof product.custom_fields['astr-swatches'] !== 'undefined') {
    swatches_json = (typeof product.custom_fields['astr-swatches'] === 'string') ? JSON.parse(product.custom_fields['astr-swatches']) : product.custom_fields['astr-swatches'];
  }

  let images_details = [];
  if (typeof product?.custom_fields['astr-images_details'] !== 'undefined') {
    images_details = (typeof product.custom_fields['astr-images_details'] === 'string') ? JSON.parse(product.custom_fields['astr-images_details']) : product.custom_fields['astr-images_details'];
  }

  const colorMap = new Map();
  product.skus.forEach((sku) => {
    const color = sku.custom_fields?.Color;
    if (color) {
      if (!colorMap.has(color)) {
        colorMap.set(color, { isPlus: false, hasStandard: false, firstVariant: sku });
      }
      const isPlusSku = sku.name.toLowerCase().includes('plus size -');
      const entry = colorMap.get(color);
      if (isPlusSku) {
        entry.isPlus = true;
      } else {
        entry.hasStandard = true;
      }
    }
  });

  if (colorMap.size === 0) {
    console.error('[ASTR] This product has no colors defined:', product);
  }

  let unique_product_color_names = Array.from(colorMap.keys());
  const is_search = getCachedDOM('isSearch', 'body', el => el.classList.contains('has-overlay-search')) || false;
  const is_plus_collection = getCachedDOM('isPlusCollection', '.main-collection [data-collection-grid]', el => el.dataset.plus === 'true') || false;

  // We need to hide the non-plus size swatches if we are on a plus size PLP or it's search results (unless there are no plus sizes available)
  // Example: https://www.astrthelabel.com/collections/plus-size-dresses
  if (is_plus_collection) {
    const unique_product_color_names_plus_sizes = unique_product_color_names.filter((color) => colorMap.get(color).isPlus);

    if (is_search) {
      unique_product_color_names = (unique_product_color_names_plus_sizes.length === 0) ? unique_product_color_names : unique_product_color_names_plus_sizes;
    } else {
      unique_product_color_names = unique_product_color_names_plus_sizes;
    }
  } else {
    // Remove plus size colors if we are not on a plus size PLP, unless there are no standard sizes available;
    const unique_product_color_names_standard_sizes = [];
    unique_product_color_names.forEach((color_name) => {
      const entry = colorMap.get(color_name);

      if (entry.hasStandard) {
        unique_product_color_names_standard_sizes.push(color_name);
      } else {
        const standard_color_name = color_name.replace(/plus size -/i, '').trim().toLowerCase();

        let standardEntry = null;
        for (const [key, val] of colorMap.entries()) {
          if (key.toLowerCase() === standard_color_name) {
            standardEntry = val;
            break;
          }
        }

        if (!standardEntry || !standardEntry.hasStandard) {
          unique_product_color_names_standard_sizes.push(color_name);
        }
      }
    });

    if (is_search) {
      unique_product_color_names = (unique_product_color_names_standard_sizes.length === 0) ? unique_product_color_names : unique_product_color_names_standard_sizes;
    } else {
      unique_product_color_names = unique_product_color_names_standard_sizes;
    }
  }

  const unique_product_color_handles = unique_product_color_names.map((color) => handleize(color));

  // We don't show products that have no swatches available
  if (unique_product_color_handles.length === 0) {
    return '';
  }

  // Nosto sometimes returns "recommended_sku" for recommendations only (Not collections or search results), we'll use it to init the correct color swatch
  let recommended_color = '';
  if (typeof product.recommended_sku !== 'undefined') {
    recommended_color = product.recommended_sku.name.split(' / ')[0];
  }

  // Some collection have metadata used to define the featured color by product
  // Load data-collection-featured-product-color-json if exists
  // We are now loading the data with the Storefront API instead of Liquid due to large amount of metaobjects, so it might not be loaded yet for page 1
  const collection_featured_product_color_json = document.querySelector('[data-collection-featured-product-color-json]');
  // console.log('FixedFeaturedColor collection_featured_product_color_json', collection_featured_product_color_json);
  let collection_featured_product_colors = [];
  if (collection_featured_product_color_json) {
    collection_featured_product_colors = JSON.parse(collection_featured_product_color_json.textContent);

    // Find the product in the collection_featured_product_colors array
    const product_featured_color = collection_featured_product_colors.find((item) => Number(item.product_id) === Number(product.product_id));
    if (product_featured_color) {
      recommended_color = product_featured_color.color;
    }
  }

  const abTestingB = getCachedDOM('abTestingB', '.main-collection-section--settings', el => el.dataset.abTesting === 'b') || false;

  let hasEcomImage = false;
  if (abTestingB) {
    hasEcomImage = product.skus.some(
      sku => sku.custom_fields && sku.custom_fields['astr-use_ecom_image'] === 'true'
    );
  }

  let swatch_items_html = '';
  let total_swatches = 0;
  unique_product_color_names.forEach((color, index) => {
    const color_handle = unique_product_color_handles[index];

    // Find the 1st variant using this color
    const entry = colorMap.get(color);
    const first_variant = entry.firstVariant;

    // Extract the images data from the metafield
    let front_image_url = '';
    let front_image_alt = '';
    let back_image_url = '';
    let back_image_alt = '';

    images_details.find((image) => {
      if (image.color_name === color) {
        front_image_url = first_variant.image_url;
        front_image_alt = (image.front_image_alt === '') ? `${CONSTANTS.DEFAULT_ALT_TEXT.FRONT} ${product.name} ${CONSTANTS.DEFAULT_ALT_TEXT.WITH_COLOR} ${color}` : image.front_image_alt;
        back_image_url = (image.back_image_url === '') ? findBackVariantImage(product, color) : image.back_image_url;
        back_image_alt = (image.back_image_alt === '') ? `${CONSTANTS.DEFAULT_ALT_TEXT.BACK} ${product.name} ${CONSTANTS.DEFAULT_ALT_TEXT.WITH_COLOR} ${color}` : image.back_image_alt;
      }
    });

    // Case where we use special ecom images https://astrthelabel.atlassian.net/browse/ASTR-864
    // For AB testing purposes only
    // Last image becomes the 1st one and the 1st one becomes the last one for hover
    if (abTestingB && hasEcomImage) {
      const front_image_url_save = front_image_url;
      const front_image_alt_save = front_image_alt;
      front_image_url = back_image_url;
      front_image_alt = back_image_alt;
      back_image_url = front_image_url_save;
      back_image_alt = front_image_alt_save;
    }

    // Find color fill
    let swatch_json = swatches_json.find((swatch) => swatch.color_handle === color_handle);
    if (typeof swatch_json === 'undefined' && color_handle.includes('plus-size-')) {
      swatch_json = swatches_json.find((swatch) => swatch.color_handle === color_handle.replace('plus-size-', ''));
    }

    // Don't lazy load the product images above the fold
    const loading_type = (typeof tile_settings.tile_index !== 'undefined' && tile_settings.tile_index !== '' && tile_settings.tile_index <= 5) ? 'eager' : 'lazy';

    let swatch_span = `<span style="background-color: #FFFFFF;"></span>`;
    if (typeof swatch_json !== 'undefined') {
      if (swatch_json.swatch_type === 'image') {
        const swatch_img_base = escapeHtml(swatch_json.swatch_value);
        swatch_span = `<span>
          <img
            loading="${loading_type}"
            src="${swatch_img_base.replace(/(\.[a-z]+)$/, '_15x15$1')}"
            srcset="
              ${swatch_img_base.replace(/(\.[a-z]+)$/, '_15x15$1')} 15w,
              ${swatch_img_base.replace(/(\.[a-z]+)$/, '_30x30$1')} 30w,
              ${swatch_img_base.replace(/(\.[a-z]+)$/, '_60x60$1')} 60w
            "
            sizes="15px"
            alt="${escapeHtml(color)} color swatch"
            width="15"
            height="15"
            style="max-width:100%;height:auto"
          />
        </span>`;
      } else {
        swatch_span = `<span style="background-color: ${escapeHtml(swatch_json.swatch_value)};"></span>`;
      }
    }

    swatch_items_html += `
      <div
        data-color-swatch
        class="color-swatch"
        role="button"
        aria-label="Select ${escapeHtml(color)} color"
        tabindex="0"
        data-variant-id="${first_variant.id}"
        data-color-value="${escapeHtml(color)}"
        data-color-handle="${color_handle}"
        data-front="${front_image_url}"
        data-back="${back_image_url}"
        data-front-alt="${front_image_alt}"
        data-back-alt="${back_image_alt}"
        data-is-recommended="${(color === recommended_color) ? 'true' : 'false'}"
      >
        ${swatch_span}
      </div>`;

    total_swatches++;
  });

  // Don't show single swatches, we still need them to initialize the tile
  if (total_swatches < 2) {
    swatch_items_html = swatch_items_html.replace('class="color-swatch"', 'class="color-swatch visually-hidden"');
  }

  const moreColorsAriaLabel = `More colors for ${escapeHtml(product.name)} (${total_swatches} colors available)`;

  const swatches_html = `
    <div class="product-tile__colors" data-product-tile-colors>
      <div class="color-swatches" data-color-swatches>
        ${swatch_items_html}
      </div>
      <a href="${product.url}" aria-label="${moreColorsAriaLabel}" tabindex="0" class="color-swatch__more" data-total-swatches="${total_swatches}"><span>More Colors +</span></a>
    </div>
  `;

  return swatches_html;
};

const pricesForTile = (product, product_available, tile_settings) => {
  let list_price = product.list_price;
  let price = product.price;

  // If we have a sale price at variant level, we use it
  let lowest_price = 0;
  let highest_price = 0;

  product.skus.forEach((sku) => {
    lowest_price = (lowest_price === 0 || sku.price < lowest_price) ? sku.price : lowest_price;
    highest_price = (highest_price === 0 || sku.price > highest_price) ? sku.price : highest_price;
  });

  // We requests prices from Nosto in USD as the currencies in Nosto and Shopify might not be fully synced
  if (window.theme.currency_cart !== 'USD' && tile_settings.enable_price_conversion === true) {
    const rate = Number(window.Shopify.currency.rate);
    lowest_price = lowest_price * rate;
    highest_price = highest_price * rate;
    list_price = list_price * rate;
    price = price * rate;
  }

  const from_price = (highest_price > lowest_price) ? String(parseFloat(lowest_price * 100, 10)) : '';

  let price_html = '';
  if (from_price === '') {
    if (list_price > price) {
      price_html += `<span class="visually-hidden">Regular price</span>
        <span class="price__sales">
          <s class="price__compare-at">${formatMoney(Number(list_price).toFixed(2), CONSTANTS.MONEY_FORMAT)}</s>
          <span class="price__regular">${formatMoney(Number(price).toFixed(2), CONSTANTS.MONEY_FORMAT)}</span>
        </span>`;
    } else {
      price_html += `<span class="price__regular">${formatMoney(Number(price).toFixed(2), CONSTANTS.MONEY_FORMAT)}</span>`;
    }
  } else {
    price_html += `<span class="price__regular">From ${formatMoney(Number(from_price).toFixed(2), CONSTANTS.MONEY_FORMAT)}</span>`;
  }

  if (!product_available) {
    price_html += `<span class="price__sold-out">Sold Out</span>`;
  }

  return price_html;
};

// tile_settings: {is_slider: true, is_simplified: true, has_swatches: false, has_quickview: false, is_search: false, is_lookbook: false, enable_price_conversion: false}
// Simplified tiles don't have the wishlist, flags, promo messages...
// Lookbook tiles use a different grid layout
// Quickview: tiles need to have the swatches
export default (product, tile_settings, debug = false) => {
  try {
    if (typeof product === 'undefined') {
      console.log(`[ASTR] Product is missing`);
    }

    if (debug) {
      console.log(product);
    }

    if (typeof product.skus === 'undefined' || product.skus.length === 0) {
      console.log(`[ASTR] Product (id: ${product.product_id}) has no variants`);
      return;
    }

    let style = '';
    const ratingData = extractRatingData(product?.custom_fields?.['okendo-summarydata'] ?? product?.custom_fields?.['okendo-summaryData'] ?? '');
    const product_handle = product.url.split('/').pop();
    const safe_product_title = product_handle.replace(/-/g, ' ');
    const product_url = getProductURL(product_handle);
    const product_available = true;
    const has_plus_size = product.skus.some(sku => sku.name.toLowerCase().includes('plus size -'));
    const product_tags = [...product.tags1 ?? [], ...product.tags2 ?? [], ...product.tags3 ?? []];
    const primary_flag_text = (tile_settings.is_simplified === true) ? '' : getPrimaryFlagTextForTile(product, product_tags);
    const image_aria_label = escapeHtml((primary_flag_text !== '') ? `${product.name} ${primary_flag_text}` : product.name);
    const promos_html = (tile_settings.is_simplified === true) ? '' : promoForTile(product, tile_settings);
    const featured_promos_html = (tile_settings.is_simplified === true) ? '' : featuredPromoForTile(product, tile_settings);
    const flags_html = (tile_settings.is_simplified === true) ? '' : flagsForTile(product, product_tags);
    const show_wishlist = tile_settings.has_wishlist === true || (tile_settings.is_simplified !== true && tile_settings.has_wishlist !== false);
    const wishlist_html = show_wishlist ? wishlistForTile(product, product_handle) : '';
    const images_html = imagesForTile(product, safe_product_title, tile_settings);
    const add_to_cart_alt = (tile_settings.is_simplified === true) ? '' : addToCartAltForTile(product);
    const quickview_html = (tile_settings.has_quickview) ? quickviewForTile() : '';
    const swatches_html = (tile_settings.has_swatches === false && tile_settings.has_quickview === false) ? '' : swatchesForTile(product, tile_settings);
    const ratings_html = getRatingHTML(ratingData, product_url);
    const price_html = pricesForTile(product, product_available, tile_settings);
    const has_video = (product?.custom_fields['astr-product_includes_video'] === 'true') ?? false;
    const ab_video = (product?.custom_fields['astr-ab_test_video'] === 'true') ?? false;
    style += (typeof tile_settings.position !== 'undefined' && tile_settings.position !== '' && tile_settings.position !== null && tile_settings.position !== 'null') ? `order: ${tile_settings.position};` : '';
    style = (style !== '') ? ` style="${style}"` : '';

    // Used from collection-placeholder-tiles.liquid to set the tile order the same as the placeholder when marketing blocks are present
    const tile_id = (typeof tile_settings.tile_index !== 'undefined' && tile_settings.tile_index !== '') ? ` id="product-tile-${tile_settings.tile_index}"` : '';

    // Disable animations for the first 5 tiles to optimize performance
    if (typeof tile_settings.tile_index !== 'undefined' && tile_settings.tile_index <= 5 && tile_settings.tile_index !== '') {
      tile_settings.has_animations = false;
    }

    let video_status_html = '';
    if (has_video) {
      console.log(`[ASTR] Product has video: ${product_handle}`, has_video);
      video_status_html = `<div class="sr-only product-tile__video-status" aria-live="polite" data-video-status></div>`;
    }

    // Use case:
    // If we are on a non-plus size PLP, the product has both standard and plus size variants but all standard size variants are unavailable, no swatches will appear and the product will not be displayed.
    if (tile_settings.has_swatches === true && swatches_html === '') {
      return '';
    }

    return `<div class="product-tile${(tile_settings.is_slider === true) ? ' swiper-slide' : ''}${(tile_settings.is_lookbook === false && tile_settings.is_slider !== true) ? ' column-xs-6 column-sm-4' : ''}${tile_settings.has_animations !== false ? ' animate animate--fadeInUp ' : ''} ${(tile_settings.is_simplified === true && tile_settings.has_swatches === false && tile_settings.has_quickview === false) ? ' product-tile--simplified' : ''}${(has_video) ? ' has-video' : ''}"
        ${tile_id}
        data-product-url="${product_url}"
        data-product-handle="${product_handle}"
        data-product-id="${product.product_id}"
        data-product-available="${product_available}"
        data-first-variant-id="${product.skus[0].id}"
        data-product-tile
        data-quickview="${tile_settings.has_quickview}"
        data-plus-size="${has_plus_size}"
        data-rating-count="${ratingData?.review_count || 0}"
        data-rating-average="${ratingData?.reviews_average || 0}"
        ${(has_video) ? 'data-video-init="false" data-video-observer="false"' : ''}
        ${(ab_video) ? 'data-ab-video="true"' : ''}
        tabindex="0"
        ${style}
        aria-label="${safe_product_title}">
        ${video_status_html}
        ${wishlist_html}
        
        <div class="product-tile__image-container">
          <a class="product-tile__image" href="${product_url}" data-product-tile-image aria-label="${image_aria_label}">
            ${images_html}
            ${add_to_cart_alt}
            ${flags_html}
          </a>
          ${quickview_html}
        </div>

        <div class="product-tile__bottom">
          <p class="product-tile__title" data-product-tile-title>
            <a href="${product_url}" data-product-tile-link><span>${product.name}</span></a>
          </p>

          ${ratings_html}

          <p class="product-tile__price price" data-product-tile-price>
            ${price_html}
          </p>

          ${featured_promos_html}

          ${promos_html}
          
          ${swatches_html}
        </div>
      </div>`;
  } catch (e) {
    console.error(e);
    console.error(product);
    return '';
  }
};
